import { Space } from 'antd';
import { Button, Table, Paging, usePaging, EllipsisSpan, useUpdate, PopConfirm } from '@maxtropy/components';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  apiV2EnergyConsumptionPlanDeletePost,
  V2EnergyConsumptionPlanListPostResponse,
  apiV2EnergyConsumptionPlanPagePost,
} from '@maxtropy/device-customer-apis-v2';
import { SearchProps } from '..';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { PlusOutlined } from '@ant-design/icons';

const columns = [
  {
    title: '部门',
    dataIndex: 'ownerOrgName',
    width: 140,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '区域',
    dataIndex: 'zoneName',
    width: 200,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '申请人',
    dataIndex: 'applyUserName',
    width: 140,
    ellipsis: { showTitle: true },
    render: (value: string) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '申请时间范围',
    dataIndex: 'timeRange',
    width: 200,
    ellipsis: { showTitle: true },
    render: (value: string) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '申请能源介质',
    dataIndex: 'energyMedium',
    width: 250,
    ellipsis: { showTitle: true },
    render: (value: string) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '申请时间',
    dataIndex: 'applyTime',
    width: 200,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v && dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
];

type DataItem = V2EnergyConsumptionPlanListPostResponse['list'];
interface PlanProps {
  searchParams: SearchProps;
}

const EnergySourcesPlan: FC<PlanProps> = props => {
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const { searchParams } = props;
  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;

  const [planData, setPlanData] = useState<DataItem>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [updateState, updateFn] = useUpdate();
  const hasEnergyUsePlan = useHasPermission(PermissionsType.B_NEWENERGYUSEPLAN);

  useEffect(() => {
    setLoading(true);
    apiV2EnergyConsumptionPlanPagePost({
      ...searchParams,
      page: pageOffset,
      size: pageSize,
      startTime: searchParams?.time ? dayjs(searchParams?.time[0]).startOf('day').valueOf() : undefined,
      endTime: searchParams?.time ? dayjs(searchParams?.time[1]).endOf('day').valueOf() : undefined,
    })
      .then(res => {
        setPlanData(res.list);
        setTotalCount(res?.total ?? 0);
      })
      .finally(() => setLoading(false));
  }, [searchParams, updateState, setTotalCount, pageOffset, pageSize]);

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 160,
      fixed: 'right' as const,
      render: (value: undefined, record: any) => {
        return (
          <Space size={16}>
            {/* <Button type="link">导出</Button> */}
            <Button type="link" href={`/api/v2/energy/consumption/plan/export?id=${record.id}`} download>
              导出
            </Button>
            <Button
              type="link"
              onClick={() => navigate(`/energy/usage/application/detail/${record.id}?${urlSearchParams.toString()}`)}
            >
              查看
            </Button>
            {record.editable && (
              <>
                <Button
                  type="link"
                  onClick={() => navigate(`/energy/usage/application/edit/${record.id}?${urlSearchParams.toString()}`)}
                >
                  编辑
                </Button>
                <PopConfirm
                  placement="top"
                  title="你确定要删除吗？"
                  onConfirm={() => {
                    onHandleDelete(record.id);
                  }}
                >
                  <Button type="link">删除</Button>
                </PopConfirm>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const onHandleDelete = (v: number) => {
    apiV2EnergyConsumptionPlanDeletePost({ id: v }).then(res => {
      updateFn();
    });
  };

  return (
    <div style={{ background: 'var(--mx-module-bg-color)', flex: 1 }}>
      {hasEnergyUsePlan && (
        <Space size={8} style={{ marginBottom: 10 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/energy/usage/application/create?${urlSearchParams.toString()}`)}
          >
            用能计划申请
          </Button>
        </Space>
      )}
      <Table loading={loading} columns={buildColumns} dataSource={planData} scroll={{ x: 1400 }} />
      <Paging pagingInfo={pagingInfo} />
    </div>
  );
};

export default EnergySourcesPlan;
